import { YesObject } from '@yes.technology/react-toolkit'
import { useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18n-lite'
import { copyToClipboard } from 'shared/utils'
import useFormattedValue from '../../hooks/useFormattedValue'
import useTableContext from '../../hooks/useTableContext'
import EditableCellInput from './EditableCellInput'
import { useCursorState } from './hooks/useCursorState'
import useCustomTableCell, { CustomActions } from './hooks/useCustomTableCell'
import { CURSOR_STATES } from './TableCell.constants'
import { EditableCell, StyledTableCell } from './TableCell.styles'
import { TableCellProps } from './TableCell.types'
import TableCellContent from './TableCellContent'

export default function TableCell({
  columnName,
  value,
  objectUuidOrIndex,
  dateOptions,
  rowBackgroundColor,
  enablePreview = false
}: TableCellProps) {
  const {
    columnsLayouts,
    extraColumns,
    fields,
    mode,
    selectedRows,
    objectclass,
    mapColumnToAction
  } = useTableContext()

  const cellRef = useRef<HTMLTableCellElement>(null)

  const customAction = useCustomTableCell({
    columnName,
    value,
    mapColumnToAction,
    objectclass
  })

  const isCustomAction = customAction?.type === CustomActions.link_action

  const {
    cursorState,
    getCursorStyle,
    handleMouseEnter,
    handleMouseLeave,
    setCopied
  } = useCursorState({ ref: cellRef, enabled: !isCustomAction })

  const { t } = useTranslation()

  const { type } = fields?.[columnName] || {}
  const formattedValue = useFormattedValue({ value, type, dateOptions })
  const columnLayout = columnsLayouts.find(
    (layout) => layout.name === columnName
  )

  const extraColumnsLayout = extraColumns
    ?.filter((column) => typeof column === 'object' && 'field_names' in column)
    .find((column) => column.field_names.includes(columnName))

  const textWrapping = columnLayout?.text_wrapping

  const copyValueToClipboard = useCallback(() => {
    if (formattedValue && !isCustomAction) {
      copyToClipboard(formattedValue)
      setCopied()
    }
  }, [formattedValue, setCopied, isCustomAction])

  const isCellTextCopied = cursorState === CURSOR_STATES.COPIED
  const enableTextFading =
    !!(columnLayout?.text_fading && columnLayout.width) ||
    !!(extraColumnsLayout?.text_fading && extraColumnsLayout.width)
  const textFadingBackgroundColor = isCellTextCopied
    ? undefined
    : rowBackgroundColor
  const selectedRowsArray = Object.keys(selectedRows ?? {})
  const isSelected = selectedRowsArray.includes(objectUuidOrIndex)
  const isEditable =
    mode === 'insert' ||
    (mode === 'edit' && (isSelected || !selectedRowsArray.length))

  if (isEditable) {
    return (
      <EditableCell ref={cellRef}>
        <EditableCellInput
          value={value}
          columnName={columnName}
          objectUuidOrIndex={objectUuidOrIndex}
          formattedValue={formattedValue}
        />
      </EditableCell>
    )
  }

  const isURLObjectclass =
    objectclass?.relation_classes?.[columnName]?.uuid_objectclass ===
    globalThis.urlObjectclassUuid

  return (
    <StyledTableCell
      ref={isCustomAction ? null : cellRef}
      hasTextWrapping={Boolean(textWrapping)}
      onDoubleClick={copyValueToClipboard}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ cursor: getCursorStyle() }}
      title={isCustomAction ? '' : t('data-tables.double-click')}
      role='cell'
      aria-label={`${columnName}: ${formattedValue}`}
      data-testid={`table-cell-${columnName}`}
      enableTextFading={enableTextFading}
      columnWidth={columnLayout?.width || extraColumnsLayout?.width}
    >
      <TableCellContent
        customAction={isCustomAction ? customAction : null}
        formattedValue={formattedValue}
        value={value as YesObject[]}
        enablePreview={enablePreview}
        objectUuidOrIndex={isURLObjectclass ? objectUuidOrIndex : undefined}
        isCellTextCopied={isCellTextCopied}
        enableTextFading={enableTextFading}
        textFadingBackgroundColor={textFadingBackgroundColor}
      />
    </StyledTableCell>
  )
}
